import {useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import EmptyData from "@/components/EmptyData";

import {stdev} from "@/utils/functions";

import "./RpeTab.scss";

// ── RPE zone config ───────────────────────────────────────────────────────────

const rpeZone = (v) => {
	const n = parseInt(v);
	if (!n) return {cls: "none", label: "—"};
	if (n <= 3) return {cls: "low", label: "Basso"};
	if (n <= 6) return {cls: "moderate", label: "Moderato"};
	if (n <= 8) return {cls: "hard", label: "Pesante"};
	return {cls: "vhard", label: "Molto pesante"};
};

// ── Distribution bar ──────────────────────────────────────────────────────────

function DistributionBar({rpeEntries}) {
	const counts = useMemo(() => {
		const c = Array(10).fill(0);
		rpeEntries.forEach((r) => {
			const n = parseInt(r.rpe);
			if (n >= 1 && n <= 10) c[n - 1]++;
		});
		return c;
	}, [rpeEntries]);

	const max = Math.max(...counts, 1);

	return (
		<div className="rpe-dist">
			{counts.map((count, i) => {
				const v = i + 1;
				const zone = rpeZone(v);
				const pct = Math.round((count / max) * 100);
				return (
					<div
						key={v}
						className={"rpe-dist__col rpe-tone--" + zone.cls}
						title={`RPE ${v}: ${count}`}
					>
						<div className="rpe-dist__bar-wrap">
							<div className="rpe-dist__bar" style={{height: `${pct}%`}} />
						</div>
						<span className="rpe-dist__label">{v}</span>
						{count > 0 && <span className="rpe-dist__count">{count}</span>}
					</div>
				);
			})}
		</div>
	);
}

// ── KPI strip ─────────────────────────────────────────────────────────────────

function KpiStrip({rpeEntries, total}) {
	const avg = useMemo(() => {
		const nonGk = rpeEntries.filter((r) => !r.goalkeeper);
		if (!nonGk.length) return null;
		return (
			nonGk.reduce((s, r) => s + parseInt(r.rpe), 0) / nonGk.length
		).toFixed(1);
	}, [rpeEntries]);

	const sd = useMemo(() => {
		const vals = rpeEntries.map((r) => parseInt(r.rpe));
		return vals.length >= 2 ? stdev(vals) : null;
	}, [rpeEntries]);

	const zone = rpeZone(avg ? Math.round(parseFloat(avg)) : null);
	const answered = rpeEntries.length;

	return (
		<div className="rpe-kpi">
			{/* Zone chip */}
			<div className={"rpe-kpi__zone rpe-tone--" + zone.cls}>
				<span className="rpe-kpi__zone-dot" />
				Carico {zone.label}
			</div>

			<div className="rpe-kpi__sep" />

			{/* Average */}
			<div className="rpe-kpi__stat">
				<span className={"rpe-kpi__value rpe-tone--" + zone.cls}>
					{avg ?? "—"}
				</span>
				<span className="rpe-kpi__desc">media (no POR)</span>
			</div>

			{/* Std dev */}
			<div className="rpe-kpi__stat">
				<span className="rpe-kpi__value rpe-kpi__value--neutral">
					{sd ?? "—"}
				</span>
				<span className="rpe-kpi__desc">dev. standard</span>
			</div>

			<div className="rpe-kpi__sep" />

			{/* Response count */}
			<div className="rpe-kpi__responses">
				<span
					className={
						"rpe-kpi__value rpe-tone--" +
						(answered < total ? "moderate" : "low")
					}
				>
					{answered}/{total}
				</span>
				<span className="rpe-kpi__desc">risposte</span>
			</div>
		</div>
	);
}

// ── Player RPE row ─────────────────────────────────────────────────────────────

function RpeRow({player, rpeEntry, onEdit, onDelete}) {
	const userRole = useSelector((s) => s?.user?.roleName);
	const navigate = useNavigate();
	const zone = rpeZone(rpeEntry?.rpe ?? null);

	return (
		<div className="rpe-row">
			{/* Player info */}
			<div className="rpe-row__info">
				<span
					className="rpe-row__name"
					onClick={() => navigate("/players/" + player.id)}
					title="Vai al profilo"
				>
					{player.firstName} {player.lastName}
				</span>
				<span className="rpe-row__role">{player.primaryRole}</span>
			</div>

			{/* RPE badge */}
			{rpeEntry ? (
				<span className={"rpe-badge rpe-tone--" + zone.cls}>
					<strong>{rpeEntry.rpe}</strong>
					<span>{zone.label}</span>
				</span>
			) : (
				<span className="rpe-badge rpe-badge--missing">non risposto</span>
			)}

			{/* Actions (hover) */}
			{userRole !== "Analyst" && rpeEntry && (
				<div className="rpe-row__actions">
					<button className="rpe-row__btn" onClick={() => onEdit(rpeEntry)}>
						<EditIcon sx={{fontSize: 14}} />
					</button>
					<button
						className="rpe-row__btn rpe-row__btn--delete"
						onClick={() => onDelete(rpeEntry)}
					>
						<CloseIcon sx={{fontSize: 14}} />
					</button>
				</div>
			)}
		</div>
	);
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader({label, count}) {
	return (
		<div className="rpe-section-header">
			<span className="rpe-section-header__label">{label}</span>
			<span className="rpe-section-header__count">{count}</span>
		</div>
	);
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function RpeTab({matchData, teamPlayers, onEdit, onDelete}) {
	// All convocated + present internal players
	const presentIds = useMemo(() => {
		const s = new Set();
		(matchData?.matchConvocations ?? []).forEach((c) => {
			if (c.status === "Presente") s.add(c.playerId);
		});
		return s;
	}, [matchData?.matchConvocations]);

	const presentPlayers = useMemo(
		() =>
			(teamPlayers ?? [])
				.filter((p) => presentIds.has(p.id))
				.sort((a, b) => a.lastName.localeCompare(b.lastName)),
		[teamPlayers, presentIds],
	);

	// Build rpe map
	const rpeMap = useMemo(() => {
		const m = {};
		(matchData?.matchRpe ?? []).forEach((r) => {
			m[r.playerId] = r;
		});
		return m;
	}, [matchData?.matchRpe]);

	const answered = presentPlayers.filter((p) => rpeMap[p.id]);
	const missing = presentPlayers.filter((p) => !rpeMap[p.id]);

	// Players not in presentPlayers but who have an RPE entry (e.g. manual staff entry)
	const extraEntries = (matchData?.matchRpe ?? []).filter(
		(r) => !presentIds.has(r.playerId),
	);
	const extraPlayers = extraEntries.map((r) => {
		const p = (teamPlayers ?? []).find((pl) => pl.id === r.playerId);
		return {player: p, rpeEntry: r};
	});

	if (!presentPlayers.length && !extraPlayers.length) {
		return <EmptyData />;
	}

	return (
		<div className="rpe-wrap">
			{/* KPI strip */}
			{(matchData?.matchRpe?.length ?? 0) > 0 && (
				<>
					<KpiStrip
						rpeEntries={matchData.matchRpe}
						total={presentPlayers.length}
					/>
					<DistributionBar rpeEntries={matchData.matchRpe} />
				</>
			)}

			{/* Answered */}
			{answered.length > 0 && (
				<div className="rpe-group">
					<SectionHeader label="Risposte" count={answered.length} />
					{answered.map((p) => (
						<RpeRow
							key={p.id}
							player={p}
							rpeEntry={rpeMap[p.id]}
							onEdit={onEdit}
							onDelete={onDelete}
						/>
					))}
				</div>
			)}

			{/* Extra (staff-entered, not convocated) */}
			{extraPlayers.length > 0 && (
				<div className="rpe-group">
					<SectionHeader label="Altri" count={extraPlayers.length} />
					{extraPlayers.map(({player, rpeEntry}) =>
						player ? (
							<RpeRow
								key={rpeEntry.id}
								player={player}
								rpeEntry={rpeEntry}
								onEdit={onEdit}
								onDelete={onDelete}
							/>
						) : null,
					)}
				</div>
			)}

			{/* Missing */}
			{missing.length > 0 && (
				<div className="rpe-group">
					<SectionHeader label="Non risposto" count={missing.length} />
					{missing.map((p) => (
						<RpeRow
							key={p.id}
							player={p}
							rpeEntry={null}
							onEdit={onEdit}
							onDelete={onDelete}
						/>
					))}
				</div>
			)}
		</div>
	);
}
